import {
  createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState,
  type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent, type ReactNode,
} from 'react'
import { ChevronLeft, ChevronRight, ClipboardCopy, ExternalLink, FileText } from 'lucide-react'
import { Floating } from './floating.js'
import { navigate } from '../router.js'
import { usePrefsStore } from '../prefs.js'
import { Modal } from '../admin/primitives.js'
import { estPageCourante, lienAbsolu } from './menu-contextuel-outils.js'

// Le socle du lot 2 : outils purs, testés sans navigateur.
export { composerDeclencheurs, estPageCourante, lienAbsolu } from './menu-contextuel-outils.js'
export type { AdresseAffichee, DeclencheursComposes, GestionnairesHote } from './menu-contextuel-outils.js'

/**
 * MENU CLIC DROIT (24/10/2026) — UN menu pour toutes les pages de Gestion.
 *
 * Règle d'Hugo : le menu ne crée AUCUN pouvoir. Il reprend des gestes qui
 * existent déjà ailleurs, avec les mêmes droits, le même mode sûr et les
 * mêmes confirmations — il fait seulement gagner un clic. Le serveur reste
 * l'arbitre : une entrée affichée ne vaut jamais autorisation.
 *
 * Mécanique :
 *   · chaque élément déclare SES actions (`useMenuContextuel()(construire)`),
 *     construites au moment de l'ouverture, jamais au rendu ;
 *   · un seul menu à l'écran, monté une fois par le shell ;
 *   · ouverture au clic droit, à la touche Menu ou Maj+F10, et à l'appui
 *     long sur un écran tactile ;
 *   · le menu du navigateur reste partout où rien n'est déclaré, et sur
 *     Maj + clic droit, pour qui veut « Inspecter » ou « Copier le lien » ;
 *   · la préférence `context_menu` (Apparence, défaut de l'organisation
 *     dans Général) le coupe entièrement.
 *
 * Un sous-menu (« Changer de salle ») remplace la liste, avec « Retour » en
 * tête : même geste au doigt, à la souris et au clavier, et aucun panneau
 * latéral qui sortirait de l'écran d'un téléphone.
 */

export interface ActionMenu {
  id: string
  libelle: string
  icone?: ReactNode
  /** Geste immédiat. Le menu est fermé AVANT l'appel. */
  agir?: () => void | Promise<void>
  /** Navigation interne. */
  href?: string
  /** Ouvre `href` dans un nouvel onglet (navigation hors Gestion comprise). */
  nouvelOnglet?: boolean
  sousMenu?: ActionMenu[]
  /** L'entrée reste visible mais grisée, avec la raison en clair. */
  indisponible?: string
  danger?: boolean
  /** Coche (sous-menu : la salle actuelle). */
  coche?: boolean
}

export interface GroupeMenu { titre?: string; actions: ActionMenu[] }

export type ConstructeurMenu = () => GroupeMenu[]

export interface DeclencheursMenu {
  onContextMenu: (e: ReactMouseEvent<HTMLElement>) => void
  onKeyDown: (e: ReactKeyboardEvent<HTMLElement>) => void
  onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void
  onPointerUp: () => void
  onPointerMove: (e: ReactPointerEvent<HTMLElement>) => void
  onPointerCancel: () => void
  onClickCapture: (e: ReactMouseEvent<HTMLElement>) => void
  'data-menu-contextuel': ''
}

interface Ouverture { n: number; x: number; y: number; titre: string | null; groupes: GroupeMenu[]; origine: HTMLElement | null; retour: HTMLElement | null; parAppui: boolean }

type Ouvrir = (construire: ConstructeurMenu, ou: { x: number; y: number }, origine: HTMLElement | null, titre?: string, parAppui?: boolean) => void

const MenuCtx = createContext<{ ouvrir: Ouvrir; actif: boolean } | null>(null)

/**
 * Une entrée « … » (Renommer…, Ajouter une note…) ouvre une petite fenêtre :
 * le menu n'a pas de place pour un champ de saisie. Une seule à la fois,
 * montée par le provider pour survivre à la fermeture du menu.
 */
export type OuvrirDialogue = (titre: string, contenu: (fermer: () => void) => ReactNode) => void
const DialogueCtx = createContext<OuvrirDialogue>(() => undefined)
export const useDialogueMenu = (): OuvrirDialogue => useContext(DialogueCtx)

const APPUI_LONG_MS = 520
const ENTREE = '[role="menuitem"], [role="menuitemradio"]'
const TOLERANCE_DOIGT_PX = 10

/**
 * Une ancre réduite à un point de l'écran — fermée au défilement.
 *
 * `contient` : ce que `Floating` ne doit pas prendre pour un clic extérieur.
 * Au lever du doigt après un appui long, iOS (et Android quand le délai
 * d'appui est rallongé) envoie un mousedown puis un clic SYNTHÉTIQUES au
 * point de départ — hors du panneau, qui s'ouvre à côté. Sans cette garde,
 * le menu se refermait aussitôt ouvert.
 */
function ancrePoint(x: number, y: number, contient: (n: Node) => boolean): HTMLElement {
  return {
    getBoundingClientRect: () => ({ top: y, bottom: y, left: x, right: x, width: 0, height: 0, x, y, toJSON: () => ({}) }),
    contains: contient,
  } as unknown as HTMLElement
}

/** Un lien imbriqué sans menu à lui garde le menu du navigateur (ouvrir dans un onglet…). */
function surLienImbrique(e: ReactMouseEvent<HTMLElement>): boolean {
  const t = e.target instanceof Element ? e.target : null
  const lien = t?.closest('a[href]')
  return !!lien && lien !== e.currentTarget && e.currentTarget.contains(lien)
}

/**
 * PORTAIL — React fait remonter les événements d'un portail (tiroir, fenêtre,
 * recadrage de photo) le long de l'arbre React, pas du DOM. Une cible qui
 * n'est pas DANS l'élément n'est pas à lui : le menu ne s'en mêle pas.
 */
function horsHote(e: { target: EventTarget | null; currentTarget: HTMLElement }): boolean {
  const t = e.target
  return !(t instanceof Node) || !e.currentTarget.contains(t)
}

/** Appui long sur un élément intérieur qui a son propre menu, ou sur un lien : c'est à lui. */
function surImbrique(e: { target: EventTarget | null; currentTarget: HTMLElement }): boolean {
  const t = e.target instanceof Element ? e.target : null
  const el = t?.closest('[data-menu-contextuel], a[href]')
  return !!el && el !== e.currentTarget && e.currentTarget.contains(el)
}

/**
 * PLEIN ÉCRAN (Mode soirée) — le menu et ses fenêtres sont montés dans
 * <body>, hors de l'élément plein écran : ils seraient invisibles. Le
 * navigateur garde alors la main.
 */
const pleinEcranAilleurs = (): boolean =>
  typeof document !== 'undefined' && !!document.fullscreenElement && document.fullscreenElement !== document.documentElement

const visibles = (g: GroupeMenu[]) => g.map((x) => ({ ...x, actions: x.actions.filter(Boolean) })).filter((x) => x.actions.length > 0)

/**
 * Les déclencheurs à étaler sur l'élément qui porte le menu :
 *
 *     const menu = useMenuContextuel()
 *     <Link href=… {...menu(() => actionsAppareil(a, ctx), a.display_name)}>
 *
 * Sans provider (page hors shell) ou préférence coupée : des déclencheurs
 * inertes, le menu du navigateur reste.
 */
export function useMenuContextuel(): (construire: ConstructeurMenu, titre?: string) => DeclencheursMenu | Record<string, never> {
  const ctx = useContext(MenuCtx)
  const appui = useRef<{ t: ReturnType<typeof setTimeout>; x: number; y: number } | null>(null)
  const ouvertParAppui = useRef(false)
  return useCallback((construire: ConstructeurMenu, titre?: string) => {
    if (!ctx?.actif) return {}
    const ouvrir = ctx.ouvrir
    const annulerAppui = () => { if (appui.current) { clearTimeout(appui.current.t); appui.current = null } }
    return {
      'data-menu-contextuel': '',
      onContextMenu: (e) => {
        // Maj + clic droit : le menu du navigateur, toujours.
        if (e.shiftKey || horsHote(e) || surLienImbrique(e) || pleinEcranAilleurs()) return
        annulerAppui()
        if (visibles(construire()).length === 0) return
        e.preventDefault()
        e.stopPropagation()
        ouvrir(construire, { x: e.clientX, y: e.clientY }, e.currentTarget, titre)
      },
      onKeyDown: (e) => {
        if (e.key !== 'ContextMenu' && !(e.key === 'F10' && e.shiftKey)) return
        if (horsHote(e) || pleinEcranAilleurs()) return
        if (visibles(construire()).length === 0) return
        e.preventDefault()
        e.stopPropagation()
        const r = e.currentTarget.getBoundingClientRect()
        ouvrir(construire, { x: r.left + Math.min(24, r.width / 2), y: r.bottom }, e.currentTarget, titre)
      },
      /*
       * APPUI LONG — Android envoie déjà `contextmenu` après un appui long ;
       * iOS Safari jamais. Le minuteur couvre iOS, et `ouvrir` est idempotent
       * pour Android (même point, même menu). Le clic qui suit le lever du
       * doigt est mangé : sinon il suivrait le lien sous le menu.
       */
      onPointerDown: (e) => {
        // Un élément intérieur qui a son menu (ou un lien) arme le sien : pas
        // de second minuteur ici, qui gagnerait en partant le dernier. On ne
        // touche pas non plus au minuteur en cours : il peut être le sien.
        // Chaque appui commence un geste neuf : la garde du clic fantôme repart à zéro.
        ouvertParAppui.current = false
        if (horsHote(e) || surImbrique(e)) return
        if (e.pointerType !== 'touch' || pleinEcranAilleurs()) return
        const el = e.currentTarget
        const x = e.clientX, y = e.clientY
        annulerAppui()
        appui.current = {
          x, y,
          t: setTimeout(() => {
            appui.current = null
            if (visibles(construire()).length === 0) return
            ouvertParAppui.current = true
            ouvrir(construire, { x, y }, el, titre, true)
          }, APPUI_LONG_MS),
        }
      },
      onPointerMove: (e) => {
        const a = appui.current
        if (a && Math.hypot(e.clientX - a.x, e.clientY - a.y) > TOLERANCE_DOIGT_PX) annulerAppui()
      },
      onPointerUp: annulerAppui,
      onPointerCancel: annulerAppui,
      onClickCapture: (e) => {
        if (!ouvertParAppui.current || horsHote(e)) return
        ouvertParAppui.current = false
        e.preventDefault()
        e.stopPropagation()
      },
    } satisfies DeclencheursMenu
  }, [ctx])
}

export function MenuContextuelProvider({ children }: { children: ReactNode }) {
  const { resolved } = usePrefsStore()
  const actif = resolved['context_menu'] !== 'off'
  const [ouvert, setOuvert] = useState<Ouverture | null>(null)
  const compteur = useRef(0)

  const ouvrir = useCallback<Ouvrir>((construire, ou, origine, titre, parAppui = false) => {
    const groupes = visibles(construire())
    if (groupes.length === 0) return
    compteur.current += 1
    // Le focus d'avant (un bouton dans une carte, une ligne…) : c'est à lui
    // qu'on le rend, l'hôte du menu n'est souvent pas focalisable.
    const avant = typeof document !== 'undefined' ? document.activeElement : null
    const retour = avant instanceof HTMLElement && avant !== document.body && (!origine || origine.contains(avant)) ? avant : null
    setOuvert({ n: compteur.current, x: ou.x, y: ou.y, titre: titre ?? null, groupes, origine, retour, parAppui })
  }, [])

  // Préférence coupée pendant qu'un menu est ouvert : il se ferme.
  useEffect(() => { if (!actif) setOuvert(null) }, [actif])

  const [dialogue, setDialogue] = useState<{ titre: string; contenu: (fermer: () => void) => ReactNode; ouvert: boolean } | null>(null)
  const ouvrirDialogue = useCallback<OuvrirDialogue>((titre, contenu) => setDialogue({ titre, contenu, ouvert: true }), [])
  const fermerDialogue = useCallback(() => setDialogue((d) => (d ? { ...d, ouvert: false } : d)), [])

  const valeur = useMemo(() => ({ ouvrir, actif }), [ouvrir, actif])
  return (
    <MenuCtx.Provider value={valeur}>
      <DialogueCtx.Provider value={ouvrirDialogue}>
        {children}
        {ouvert ? <Menu key={ouvert.n} o={ouvert} fermer={() => setOuvert(null)} /> : null}
        {dialogue ? (
          <Modal open={dialogue.ouvert} onClose={fermerDialogue} onExited={() => setDialogue(null)} owning ariaLabel={dialogue.titre} width={480}>
            <header className="lc-modal__head"><h2 className="lc-modal__title">{dialogue.titre}</h2></header>
            <div className="lc-modal__body">{dialogue.contenu(fermerDialogue)}</div>
          </Modal>
        ) : null}
      </DialogueCtx.Provider>
    </MenuCtx.Provider>
  )
}

function Menu({ o, fermer }: { o: Ouverture; fermer: () => void }) {
  const [pile, setPile] = useState<{ titre: string; actions: ActionMenu[] }[]>([])
  const liste = useRef<HTMLUListElement>(null)
  // Ouvert à l'appui long : le mousedown et le clic synthétiques du lever du
  // doigt, posés sur l'élément d'origine, ne ferment pas le menu.
  const gardeAppui = useRef(o.parAppui)
  const ancre = useMemo(() => ({
    current: ancrePoint(o.x, o.y, (n) => gardeAppui.current && !!o.origine?.contains(n)),
  }), [o])
  const niveau = pile[pile.length - 1] ?? null

  useEffect(() => {
    if (!o.parAppui) return
    const lever = () => { setTimeout(() => { gardeAppui.current = false }, 0) }
    document.addEventListener('click', lever, { capture: true, once: true })
    const filet = setTimeout(() => { gardeAppui.current = false }, 1500)
    return () => { document.removeEventListener('click', lever, { capture: true }); clearTimeout(filet) }
  }, [o.parAppui])

  const fermerEtRendre = useCallback(() => {
    fermer()
    // Le focus revient là où il était, sinon à l'élément qui portait le menu.
    const el = o.retour && document.contains(o.retour) ? o.retour : o.origine
    if (el && typeof el.focus === 'function' && document.contains(el)) el.focus({ preventScroll: true })
  }, [fermer, o.origine, o.retour])

  // Focus sur la première entrée active à chaque niveau.
  useLayoutEffect(() => {
    const t = requestAnimationFrame(() => liste.current?.querySelector<HTMLElement>(`${ENTREE}:not([aria-disabled="true"])`)?.focus())
    return () => cancelAnimationFrame(t)
  }, [niveau])

  // Les entrées grisées restent atteignables au clavier : leur raison se lit.
  const entrees = () => [...(liste.current?.querySelectorAll<HTMLElement>(ENTREE) ?? [])]
  const auClavier = (e: ReactKeyboardEvent<HTMLUListElement>) => {
    // Les raccourcis de la page (j/k, flèches, Suppr du planning) ne doivent
    // pas agir derrière un menu ouvert. Échap ferme le menu, et lui seul : un
    // tiroir qui écoute Échap sur document ne se ferme pas avec lui.
    e.stopPropagation()
    if (e.key === 'Escape') { e.preventDefault(); if (pile.length) setPile((p) => p.slice(0, -1)); else fermerEtRendre(); return }
    const tous = entrees()
    const i = tous.indexOf(document.activeElement as HTMLElement)
    const aller = (j: number) => { e.preventDefault(); tous[(j + tous.length) % tous.length]?.focus() }
    if (e.key === 'ArrowDown') aller(i + 1)
    else if (e.key === 'ArrowUp') aller(i < 0 ? tous.length - 1 : i - 1)
    else if (e.key === 'Home') aller(0)
    else if (e.key === 'End') aller(tous.length - 1)
    else if (e.key === 'ArrowLeft' && pile.length) { e.preventDefault(); setPile((p) => p.slice(0, -1)) }
    else if (e.key === 'ArrowRight') {
      const el = document.activeElement as HTMLElement | null
      if (el?.dataset['sousMenu'] !== undefined) { e.preventDefault(); el.click() }
    } else if (e.key === 'Tab') { e.preventDefault(); fermerEtRendre() }
  }

  const choisir = (a: ActionMenu) => {
    if (a.indisponible) return
    if (a.sousMenu) { setPile((p) => [...p, { titre: a.libelle, actions: a.sousMenu! }]); return }
    fermerEtRendre()
    if (a.href && a.nouvelOnglet) { window.open(a.href, '_blank', 'noopener,noreferrer'); return }
    if (a.href) { navigate(a.href); return }
    void a.agir?.()
  }

  const entree = (a: ActionMenu) => (
    <li role="none" key={a.id}>
      <button
        type="button" role={a.coche === undefined ? 'menuitem' : 'menuitemradio'}
        className={`lc-menu__item lc-ctx__item${a.danger ? ' lc-menu__item--danger' : ''}`}
        aria-disabled={a.indisponible ? 'true' : undefined}
        aria-haspopup={a.sousMenu ? 'menu' : undefined}
        aria-checked={a.coche === undefined ? undefined : a.coche}
        data-sous-menu={a.sousMenu ? '' : undefined}
        title={a.indisponible}
        onClick={() => choisir(a)}
      >
        <span className="lc-ctx__icone" aria-hidden="true">{a.icone}</span>
        <span className="lc-ctx__libelle">
          {a.libelle}
          {a.indisponible ? <span className="lc-ctx__pourquoi">{a.indisponible}</span> : null}
        </span>
        {a.coche ? <span className="lc-ctx__coche" aria-hidden="true">✓</span> : null}
        {a.sousMenu ? <ChevronRight size={15} className="lc-ctx__suite" aria-hidden="true" /> : null}
        {a.nouvelOnglet ? <ExternalLink size={13} className="lc-ctx__suite" aria-hidden="true" /> : null}
      </button>
    </li>
  )

  return (
    <Floating open anchorRef={ancre} onOutside={fermerEtRendre} closeOnScroll className="lc-menu lc-ctx" ariaLabel={o.titre ? `Actions : ${o.titre}` : 'Actions'}>
      {/* Clic droit DANS le menu, ou touche Menu relâchée (Windows envoie
          `contextmenu` au keyup) : jamais le menu du navigateur par-dessus. */}
      <ul ref={liste} className="lc-menu__list" role="menu" aria-label={niveau?.titre ?? o.titre ?? 'Actions'} onKeyDown={auClavier}
          onContextMenu={(e) => e.preventDefault()}>
        {niveau ? (
          <>
            <li role="none">
              <button type="button" role="menuitem" className="lc-menu__item lc-ctx__item lc-ctx__retour" onClick={() => setPile((p) => p.slice(0, -1))}>
                <span className="lc-ctx__icone" aria-hidden="true"><ChevronLeft size={15} /></span>
                <span className="lc-ctx__libelle">{niveau.titre}</span>
              </button>
            </li>
            <li role="separator" className="lc-menu__sep" />
            {niveau.actions.map(entree)}
          </>
        ) : (
          <>
            {o.titre ? <li role="presentation" className="lc-ctx__titre">{o.titre}</li> : null}
            {o.groupes.map((g, i) => (
              <li role="none" key={`g${i}`} className="lc-ctx__groupe">
                {i > 0 ? <div role="separator" className="lc-menu__sep" /> : null}
                {g.titre ? <div className="lc-menu__section" role="presentation">{g.titre}</div> : null}
                <ul role="group" className="lc-menu__list" aria-label={g.titre}>{g.actions.map(entree)}</ul>
              </li>
            ))}
          </>
        )}
      </ul>
    </Floating>
  )
}

/** Copie un texte et le dit ; un refus du navigateur est dit aussi. */
export async function copierTexte(texte: string, quoi: string, toast: (k: 'ok' | 'error', t: string) => void): Promise<void> {
  try {
    await navigator.clipboard.writeText(texte)
    toast('ok', `Copié — ${quoi} : ${texte}`)
  } catch {
    toast('error', `Le navigateur a refusé la copie. ${quoi} : ${texte}`)
  }
}

/* ─── socle du lot 2 : les gestes communs à tous les menus ─── */

/**
 * Les entrées d'un lien interne, comme celles du lot 1 : « Ouvrir… »,
 * « Ouvrir dans un nouvel onglet » et « Copier le lien » (absolu, collable
 * dans un message). Identifiants stables : `ouvrir`, `onglet`, `c-lien`.
 *
 * « Ouvrir » disparaît quand le lien mène à la page déjà affichée
 * (`estPageCourante`) : le clic ne ferait rien, ou changerait l'adresse sans
 * changer l'écran. L'onglet et la copie restent, ils servent encore.
 *
 *     groupes.push({ actions: entreesLien(cheminPorte(p.id), toast, { ouvrir: 'Ouvrir la porte' }) })
 *
 * Un chemin de Gestion seulement ; à appeler dans le constructeur du menu.
 */
export function entreesLien(
  href: string,
  toast: (k: 'ok' | 'error', t: string) => void,
  opts: { ouvrir?: string | undefined; ignorerRecherche?: boolean | undefined } = {},
): ActionMenu[] {
  const ici = estPageCourante(href, { ignorerRecherche: opts.ignorerRecherche })
  return [
    ...(ici ? [] : [{ id: 'ouvrir', libelle: opts.ouvrir ?? 'Ouvrir', icone: <FileText size={15} />, href }]),
    { id: 'onglet', libelle: 'Ouvrir dans un nouvel onglet', icone: <ExternalLink size={15} />, href, nouvelOnglet: true },
    { id: 'c-lien', libelle: 'Copier le lien', icone: <ClipboardCopy size={15} />, agir: () => copierTexte(lienAbsolu(href), 'lien', toast) },
  ]
}

/**
 * Ouvre une page puis amène une de ses sections à l'écran. Le routeur ne
 * suit pas les ancres : la section n'existe qu'une fois la page lue, on
 * l'attend (3 s au plus) avant de la faire défiler. Même logique que
 * `allerA` du menu de la supervision (menu-sonde.tsx), qui garde la sienne.
 */
export function allerAncre(chemin: string, ancre: string): void {
  navigate(`${chemin}#${ancre}`)
  const fin = Date.now() + 3000
  const chercher = () => {
    const el = document.getElementById(ancre)
    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); return }
    if (Date.now() < fin) requestAnimationFrame(chercher)
  }
  requestAnimationFrame(chercher)
}
